import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetInvitedWorkers, useGetAvailableWorkers } from 'src/api/hooks/useWorker';
import { useStoreActions } from 'src/stores';
import { PopUpEnum } from 'src/stores/NotificationStore';
import { UseWorkerFetchingProps } from './useWorkersData.types';
import { ColumnFiltersState } from '@tanstack/react-table';
import { WorkerScope } from 'src/api/hooks/useWorker.types';

const getKeysByScope = (scope: WorkerScope) => {
  const defaultKeys = ['name'];

  if (scope === WorkerScope.AVAILABLE) return [...defaultKeys, 'preferredLocations', 'interviewId'];
  if (scope === WorkerScope.INVITED) return [...defaultKeys, 'phoneNumber', 'poolStatus'];

  return defaultKeys;
};

const getFiltersByIds = ({
  filtersState,
  keys,
}: {
  filtersState?: ColumnFiltersState;
  keys: string[];
}) => {
  if (!filtersState?.length) return;

  return keys.reduce<Record<string, string | boolean>>((acc, id) => {
    const value = filtersState?.find(f => f.id === id)?.value as string;
    if (!value) return acc;

    if (id === 'interviewId') {
      if (value === 'true') {
        acc[id] = true;
        return acc;
      }
      if (value === 'false') {
        acc[id] = false;
        return acc;
      }
    }
    acc[id] = value;
    return acc;
  }, {});
};

export const useWorkersData = ({ scope, jobId, isVisible = true }: UseWorkerFetchingProps) => {
  const setPopUpData = useStoreActions(action => action.popUp.setPopUp);

  const { t } = useTranslation();

  const showErrorPopUp = () => {
    setPopUpData({
      isOpen: true,
      type: PopUpEnum.error,
      message: t('workersScreen.alerts.workers.error'),
    });
  };

  const [getAvailableWorkers, { loading: availableWorkersLoading, data: availableWorkersData }] =
    useGetAvailableWorkers({
      onError: () => {
        showErrorPopUp();
      },
    });

  const [getInvitedWorkers, { loading: invitedWorkersLoading, data: invitedWorkersData }] =
    useGetInvitedWorkers({
      onError: () => {
        showErrorPopUp();
      },
    });

  const data = scope === WorkerScope.INVITED ? invitedWorkersData : availableWorkersData;

  const getWorkersFunction =
    scope === WorkerScope.INVITED ? getInvitedWorkers : getAvailableWorkers;

  const fetchWorkers = ({
    page,
    filtersArray,
  }: {
    page: number;
    filtersArray?: ColumnFiltersState;
  }) => {
    const keys = getKeysByScope(scope);
    const filters = getFiltersByIds({
      filtersState: filtersArray,
      keys,
    });

    getWorkersFunction({ variables: { currentPage: page, jobId, filters } });
  };

  useEffect(() => {
    if (!isVisible) return;
    getWorkersFunction({
      variables: { currentPage: 1, jobId },
    });
  }, [scope, jobId, isVisible]);

  return {
    finalizedWorkers: data?.finalizedWorkers || [],
    filteredWorkersCount: data?.filteredWorkersCount || 0,
    totalWorkersCount: data?.totalWorkersCount || 0,
    totalPageCount: data?.totalPageCount || 1,

    workersLoading: availableWorkersLoading || invitedWorkersLoading,
    fetchWorkers,
  };
};
